import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DataGridComponent, GridAction, GridColumn, GridConfig } from '../../shared/data-grid/data-grid.component';
import { CodeValidationConflictCandidate } from '../dashboard.models';
import { DashboardService } from '../dashboard.service';

@Component({
  selector: 'app-conflict-candidates-page',
  standalone: true,
  imports: [CommonModule, DataGridComponent],
  templateUrl: './conflict-candidates-page.component.html',
  styleUrls: ['./conflict-candidates-page.component.css']
})
export class ConflictCandidatesPageComponent implements OnInit, AfterViewInit {
  private static readonly REDUNDANT_RECOMMENDATION =
    'Run full code assessment and review CMDB metadata for update.';

  @ViewChild('applicationTemplate') applicationTemplate!: TemplateRef<unknown>;
  @ViewChild('conflictTemplate') conflictTemplate!: TemplateRef<unknown>;
  @ViewChild('signalsTemplate') signalsTemplate!: TemplateRef<unknown>;
  @ViewChild('readinessTemplate') readinessTemplate!: TemplateRef<unknown>;

  candidates: CodeValidationConflictCandidate[] = [];
  displayedColumns: GridColumn[] = [];
  readonly gridActions: GridAction[] = [{ label: 'View Details', color: 'primary' }];
  readonly dataGridConfig: GridConfig = {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100],
    showFirstLastButtons: true,
    showColumnFilters: true,
    enableSorting: true,
    stickyHeader: true
  };
  readonly filtersLoaded = true;
  total = 0;
  loading = true;
  loadFailed = false;

  constructor(
    private dashboardService: DashboardService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.dashboardService.getCodeValidationConflictCandidates(0).subscribe({
      next: data => {
        this.candidates = data.candidates || [];
        this.total = data.total || 0;
        this.loading = false;
      },
      error: () => {
        this.loadFailed = true;
        this.loading = false;
      }
    });
  }

  ngAfterViewInit(): void {
    this.displayedColumns = [
      { key: 'appId', label: 'App ID', filterable: true, width: '120px' },
      {
        key: 'name',
        label: 'Application',
        type: 'custom',
        customTemplate: this.applicationTemplate,
        filterable: true,
        width: '240px'
      },
      {
        key: 'conflict',
        label: 'Conflict',
        type: 'custom',
        customTemplate: this.conflictTemplate,
        accessor: candidate => `${candidate.cmdbOutcome || 'Not classified'} Legacy found`,
        filterable: true,
        width: '230px'
      },
      {
        key: 'signals',
        label: 'Legacy signals found',
        type: 'custom',
        customTemplate: this.signalsTemplate,
        accessor: candidate => (candidate.repositoryLegacySignals || [])
          .flatMap(repository => repository.signals || [])
          .join(' '),
        filterable: true,
        width: '430px'
      },
      {
        key: 'lightweightCodeAssessmentScore',
        label: 'AI legacy score',
        type: 'custom',
        customTemplate: this.readinessTemplate,
        tooltip: 'AI-generated lightweight source-code legacy score (0-100).',
        filterable: true,
        width: '160px'
      }
    ];
    this.cdr.detectChanges();
  }

  recommendedNextStep(candidate: CodeValidationConflictCandidate): string | null {
    const recommendation = candidate.codeValidationRecommendation?.trim();
    return recommendation && recommendation !== ConflictCandidatesPageComponent.REDUNDANT_RECOMMENDATION
      ? recommendation
      : null;
  }

  onGridAction(event: { item: CodeValidationConflictCandidate }): void {
    this.openAssessment(event.item);
  }

  openAssessment(candidate: CodeValidationConflictCandidate): void {
    this.router.navigate(['/assessment', candidate.trackedBusinessApplicationId], {
      queryParams: { source: 'code-validation-candidates' }
    });
  }

  backToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}
